import type { StateCreator } from 'zustand';
import type { AppState } from '../types';
import { updateActiveProfile } from '../helpers';
import { defaultAthlete, defaultGoal, deepCloneAvailability, defaultAvailability } from '../constants';
import type { AthleteProfile, FitnessMetrics, TrainingGoal, RaceEvent, IntervalsCredentials, StravaTokens, StravaConfig, WeeklyAvailability, ZoneSet } from '@/types';

export interface AthleteSlice {
    athlete: AthleteProfile;
    metrics: FitnessMetrics;
    goal: TrainingGoal;
    events: RaceEvent[];
    intervals: IntervalsCredentials;
    strava: StravaTokens;
    stravaConfig: StravaConfig;
    availability: WeeklyAvailability;
    longRunDay: number;

    setAthlete: (profile: Partial<AthleteProfile>) => void;
    setMetrics: (metrics: Partial<FitnessMetrics>) => void;
    setGoal: (goal: Partial<TrainingGoal>) => void;
    setZones: (zones: Partial<AthleteProfile['zones']>) => void;
    addEvent: (event: RaceEvent) => void;
    removeEvent: (eventId: string) => void;
    updateEvent: (eventId: string, updates: Partial<RaceEvent>) => void;
    setIntervalsCredentials: (creds: Partial<IntervalsCredentials>) => void;
    setStravaTokens: (tokens: Partial<StravaTokens>) => void;
    setStravaConfig: (config: Partial<StravaConfig>) => void;
    setAvailability: (day: number, avail: Partial<WeeklyAvailability[number]>) => void;
    setAllAvailability: (avail: WeeklyAvailability) => void;
    setLongRunDay: (day: number) => void;
}

export const createAthleteSlice: StateCreator<AppState, [], [], AthleteSlice> = (set) => ({
    athlete: { ...defaultAthlete },
    metrics: {},
    goal: { ...defaultGoal },
    events: [],
    intervals: { athleteId: '' },
    strava: {},
    stravaConfig: {
        clientId: import.meta.env.VITE_STRAVA_CLIENT_ID || '',
        clientSecret: import.meta.env.VITE_STRAVA_CLIENT_SECRET || ''
    },
    availability: deepCloneAvailability(defaultAvailability),
    longRunDay: 6,

    setAthlete: (profile) => set((state) => {
        const newAthlete = { ...state.athlete, ...profile };
        return {
            athlete: newAthlete,
            ...(updateActiveProfile(state, { athlete: newAthlete }) as Partial<AppState>),
        };
    }),

    setMetrics: (metrics) => set((state) => {
        const newMetrics = { ...state.metrics, ...metrics };
        return {
            metrics: newMetrics,
            ...(updateActiveProfile(state, { metrics: newMetrics }) as Partial<AppState>),
        };
    }),

    setGoal: (goal) => set((state) => {
        const newGoal = { ...state.goal, ...goal };
        return {
            goal: newGoal,
            ...(updateActiveProfile(state, { goal: newGoal }) as Partial<AppState>),
        };
    }),

    setZones: (zones) => set((state) => {
        const defaultZones = {
            running: { power: [], heartRate: [], pace: [] } as ZoneSet,
            cycling: { power: [], heartRate: [], pace: [] } as ZoneSet,
            swimming: { power: [], heartRate: [], pace: [], swimPace: [] } as ZoneSet,
        };
        const currentZones = state.athlete.zones || defaultZones;
        // Deep merge at the sport level: preserve existing zone types
        // (e.g. heartRate, power) when only one type within a sport is updated.
        const newZones = { ...currentZones };
        if (zones) {
            if (zones.running) {
                newZones.running = { ...(currentZones.running || defaultZones.running), ...zones.running };
            }
            if (zones.cycling) {
                newZones.cycling = { ...(currentZones.cycling || defaultZones.cycling), ...zones.cycling };
            }
            if (zones.swimming) {
                newZones.swimming = { ...(currentZones.swimming || defaultZones.swimming), ...zones.swimming };
            }
        }
        const newAthlete = { ...state.athlete, zones: newZones };
        return {
            athlete: newAthlete,
            ...(updateActiveProfile(state, { athlete: newAthlete }) as Partial<AppState>),
        };
    }),

    addEvent: (event) => set((state) => {
        const newEvents = [...state.events, event];
        let newGoal = state.goal;
        if (event.priority === 'A') {
            newGoal = {
                type: 'event',
                raceDate: event.raceDate,
                raceType: event.raceType,
            };
        }
        return {
            events: newEvents,
            goal: newGoal,
            ...(updateActiveProfile(state, { events: newEvents, goal: newGoal }) as Partial<AppState>),
        };
    }),

    removeEvent: (eventId) => set((state) => {
        const newEvents = state.events.filter(e => e.id !== eventId);
        const remainingA = newEvents
            .filter(e => e.priority === 'A' && e.raceDate)
            .sort((a, b) => new Date(a.raceDate!).getTime() - new Date(b.raceDate!).getTime());

        let newGoal = state.goal;
        if (remainingA.length > 0) {
            const target = remainingA[remainingA.length - 1];
            newGoal = { type: 'event', raceDate: target.raceDate!, raceType: target.raceType };
        } else if (state.events.find(e => e.id === eventId)?.priority === 'A') {
            newGoal = { type: 'event', raceDate: '', raceType: 'Generic' };
        }

        return {
            events: newEvents,
            goal: newGoal,
            ...(updateActiveProfile(state, { events: newEvents, goal: newGoal }) as Partial<AppState>),
        };
    }),

    updateEvent: (eventId, updates) => set((state) => {
        const newEvents = state.events.map(e => e.id === eventId ? { ...e, ...updates } : e);
        const allA = newEvents
            .filter(e => e.priority === 'A' && e.raceDate)
            .sort((a, b) => new Date(a.raceDate!).getTime() - new Date(b.raceDate!).getTime());

        let newGoal = state.goal;
        if (allA.length > 0) {
            const target = allA[allA.length - 1];
            newGoal = { type: 'event', raceDate: target.raceDate!, raceType: target.raceType };
        } else {
            newGoal = { type: 'event', raceDate: '', raceType: 'Generic' };
        }

        return {
            events: newEvents,
            goal: newGoal,
            ...(updateActiveProfile(state, { events: newEvents, goal: newGoal }) as Partial<AppState>),
        };
    }),

    setIntervalsCredentials: (creds) => set((state) => {
        const newIntervals = { ...state.intervals, ...creds };

        // Update if we're viewing a profile

        if (creds.athleteId && state.activeProfileId) {
            return {
                intervals: newIntervals,
                ...(updateActiveProfile(state, { intervalsAthleteId: creds.athleteId }) as Partial<AppState>),
            };
        }

        return { intervals: newIntervals };
    }),

    setStravaTokens: (tokens) => set((state) => ({
        strava: { ...state.strava, ...tokens }
    })),

    setStravaConfig: (config) => set((state) => ({
        stravaConfig: { ...state.stravaConfig, ...config }
    })),


    setAvailability: (day, avail) => set((state) => {
        const newAvailability = {
            ...state.availability,
            [day]: { ...state.availability[day], ...avail }
        };
        return {
            availability: newAvailability,
            ...(updateActiveProfile(state, { availability: newAvailability }) as Partial<AppState>),
        };
    }),

    setAllAvailability: (avail) => set((state) => ({
        availability: avail,
        ...(updateActiveProfile(state, { availability: avail }) as Partial<AppState>),
    })),

    setLongRunDay: (day) => set((state) => ({
        longRunDay: day,
        ...(updateActiveProfile(state, { longRunDay: day }) as Partial<AppState>),
    })),
});
